import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Column } from 'primereact/column'
import type { LocalizedText } from '../../../lib/localizedText'
import { InputSwitch } from 'primereact/inputswitch'
import Badge from '../../../components/ui/Badge'
import Button from '../../../components/ui/Button'
import { formatDate } from '../../../lib/datetime'
import type { HolidayDay } from '../types'

interface ColumnOpts {
  t: TFunction
  /** İki dilli alanlar tek kolondur; değer seçili dilden gelir (`lib/localizedText`). */
  localized: LocalizedText
  /** İşlem süren satır — anahtar ve sil düğmesi kilitlenir (çift istek olmasın). */
  busyId: number | null
  onEdit: (row: HolidayDay) => void
  onToggle: (row: HolidayDay, active: boolean) => void
  onDelete: (row: HolidayDay) => void
  /** 126 — Kısıtlı Yetkili asistan: işlem kolonu hiç çizilmez; durum, rozet kolonundan okunur. */
  readOnly?: boolean
}

/**
 * Tatil Günü tablosunun sütunları: Tarih · Açıklama · Yarım Gün · Durum · işlemler.
 *
 * <p>Açıklama <b>tek kolondur</b> ve değeri seçili dilden gelir (24.09.2026 kullanıcı kararı); iki
 * ayrı TR/EN kolonu, kullanıcının okumadığı bir dili taşıyordu. Form iki alanı da düzenler.
 *
 * <p>Tarih ham ISO değil, <b>biçimlenmiş</b> gösterilir (paylaşılan {@code formatDate}) — kullanıcı
 * "2026-01-01" değil "01.01.2026" görür; biçim tek kaynaktan gelir.
 */
export function holidayDayColumns({
  t,
  localized,
  busyId,
  onEdit,
  onToggle,
  onDelete,
  readOnly = false,
}: ColumnOpts): ReactElement[] {
  const columns = [
    <Column
      key="holidayDate"
      field="holidayDate"
      header={t('holidayDays.columns.date')}
      style={{ width: '10rem' }}
      sortable
      body={(row: HolidayDay) => formatDate(row.holidayDate)}
    />,
    <Column
      key="description"
      header={t('holidayDays.columns.description')}
      body={(row: HolidayDay) => localized(row.descriptionTr, row.descriptionEn)}
    />,
    <Column
      key="halfDay"
      header={t('holidayDays.columns.halfDay')}
      style={{ width: '8rem' }}
      body={(row: HolidayDay) => (row.halfDay ? t('holidayDays.halfDay.yes') : t('holidayDays.halfDay.no'))}
    />,
    <Column
      key="status"
      header={t('holidayDays.columns.status')}
      style={{ width: '8rem' }}
      body={(row: HolidayDay) => {
        const active = row.recordStatus === 1
        return (
          <Badge tone={active ? 'green' : 'neutral'}>
            {active ? t('holidayDays.status.active') : t('holidayDays.status.passive')}
          </Badge>
        )
      }}
    />,
  ]
  if (readOnly) return columns
  return [
    ...columns,
    <Column
      key="actions"
      header={t('holidayDays.columns.actions')}
      style={{ width: '10rem' }}
      body={(row: HolidayDay) => (
        <div className="admin-row-actions">
          <InputSwitch
            className="admin-switch"
            checked={row.recordStatus === 1}
            disabled={busyId === row.id}
            onChange={(e) => onToggle(row, Boolean(e.value))}
            aria-label={row.descriptionTr}
            tooltip={t('holidayDays.actions.toggle')}
            tooltipOptions={{ position: 'bottom' }}
          />
          <Button
            icon="pi pi-pencil"
            rounded
            text
            aria-label={t('holidayDays.actions.editNamed', { name: row.descriptionTr })}
            tooltip={t('holidayDays.actions.edit')}
            tooltipOptions={{ position: 'bottom' }}
            onClick={() => onEdit(row)}
          />
          <Button
            icon="pi pi-trash"
            rounded
            text
            severity="danger"
            aria-label={t('holidayDays.actions.deleteNamed', { name: row.descriptionTr })}
            tooltip={t('holidayDays.actions.delete')}
            tooltipOptions={{ position: 'bottom' }}
            disabled={busyId === row.id}
            onClick={() => onDelete(row)}
          />
        </div>
      )}
    />,
  ]
}
